import { useState, useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";
import moment from "moment-timezone";
import socket from "../socket/socket";
import Pagination from "../layout/Pagination";
import PermissionGate from "../layout/PermissionGate";
import { formatDecimal } from "../utils/decimalFormat";

const TransactionTable = ({ merchantId, balance, showTransaction }) => {
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const dateRangePickerRef = useRef(null);
  const token = localStorage.getItem("token");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchTransactions = () => {
    if (showTransaction === true) {
      axios
        .get(
          `${process.env.REACT_APP_API_ENDPOINT}fetch-merchant-transactions?merchantId=${merchantId}&page=${page}&limit=${limit}&startDate=${start}&endDate=${end}&search=${search}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          if (res.data.code === 1) {
            if (page > 1) {
              if (res.data.data.length === 0) {
                setPage(page - 1);
              } else {
                setTransactions(res.data.data);
                setCount(res.data.count);
              }
            } else {
              setTransactions(res.data.data);
              setCount(res.data.count);
            }
          }
        })
        .catch(console.error);
    }
  };
  const exportData = async () => {
    try {
      setIsBusy(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}export-merchant-transactions`,
        {
          merchantId,
          startDate: start,
          endDate: end,
          search,
        },
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsBusy(false);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = Date.now();
      const filename = `merchant_transaction_${timestamp}.xlsx`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };
  const reset = () => {
    setPage(1);
    setStart("");
    setEnd("");
    setSearch("");
  };
  const openCalendar = () => {
    if (dateRangePickerRef.current) {
      dateRangePickerRef.current.flatpickr.open();
    }
  };
  const statusCard = (status) => {
    let badge;
    switch (status) {
      case "pending":
        badge = (
          <span className="badge text-white bg-warning">{status}</span>
        );
        break;
      case "success":
        badge = (
          <span className="badge text-white bg-success">{status}</span>
        );
        break;
      case "failed":
        badge = (
          <span className="badge text-white bg-danger">{status}</span>
        );
        break;
      case "cancelled":
        badge = (
          <span className="badge text-white bg-danger">{status}</span>
        );
        break;

      default:
        badge = "---";
        break;
    }
    return badge;
  }

  useEffect(() => {
    fetchTransactions();
  }, [page, search, start, end]);

  useEffect(() => {
    reset();
    fetchTransactions();
  }, [balance, showTransaction]);

  useEffect(() => {
    socket.on(`admin:merchantDataUpdate`, (data) => {
      if (data) {
        reset();
        fetchTransactions();
      }
    });
    return () => {
      socket.off(`admin:merchantDataUpdate`);
    };
  }, []);

  return (
    <PermissionGate required={`view_merchant_transactions`}>
      <div className="card">
        {/*begin::Card header*/}
        <div className="card-header border-0 pt-6">
          <div className="row w-100">
            <div className="col-md-6 text-start mb-3">
              <input
                type="text"
                value={search}
                autoComplete="off"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                className="form-control"
                placeholder="Transaction Id or Order Id"
              />
            </div>
            <div className="col-md-6 text-end mb-3">
              <div className="d-block d-md-flex gap-2 justify-content-end">
                <div className="d-flex position-relative mb-3">
                  <Flatpickr
                    ref={dateRangePickerRef}
                    value={
                      start !== "" && end !== ""
                        ? [new Date(start), new Date(end)]
                        : null
                    }
                    options={{ dateFormat: "d-m-Y", mode: "range" }}
                    onChange={([start, end]) => {
                      if (
                        start !== undefined &&
                        end !== undefined &&
                        typeof start === "object" &&
                        typeof end === "object"
                      ) {
                        setStart(moment(start).format("YYYY-MM-DD"));
                        setEnd(moment(end).format("YYYY-MM-DD"));
                      }
                    }}
                    className="form-control"
                    placeholder="Select date"
                  />
                  <i
                    onClick={openCalendar}
                    className="bi-duotone bi-calendar fs-5 position-absolute"
                    style={{ top: "28%", right: "5%", cursor: "pointer" }}
                  ></i>
                </div>
                <div className="mb-3">
                  <button
                    onClick={reset}
                    className="btn btn-danger w-100 w-md-auto"
                  >
                    <i className="ki-duotone ki-filter text-danger-700 fs-1">
                      <span className="path1"></span>
                      <span className="path2"></span>
                    </i>
                    Reset
                  </button>
                </div>
                <div>
                  <button
                    onClick={exportData}
                    className="btn btn-primary w-100 w-md-auto"
                    disabled={isBusy}
                  >
                    <i className="ki-duotone ki-cloud-download fs-1">
                      <span className="path1"></span>
                      <span className="path2"></span>
                    </i>
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/*end::Card header*/}

        {/*begin::Card body*/}
        <div className="card-body py-4 text-center" id="filterDataWrap">
          {/*begin::Table*/}
          {transactions?.length === 0 ? (
            <div className="text-center w-100">
              <img
                className="w-25 mx-auto py-20"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                alt="no-content"
              />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed fs-6 gy-5">
                <thead>
                  <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                    <th className="text-center min-w-100px">Transaction Id</th>
                    <th className="text-center min-w-100px">Branch</th>
                    <th className="text-center min-w-100px">Order Id</th>
                    <th className="text-center min-w-100px">Type</th>
                    <th className="text-center min-w-100px">Status</th>
                    <th className="text-center min-w-100px">Channel</th>
                    <th className="text-center min-w-100px">Notes</th>
                    <th className="text-center min-w-100px">Date</th>
                    <th className="text-center min-w-100px">Time</th>
                    <th className="text-center min-w-100px">Amount</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 fw-semibold">
                  {transactions &&
                    transactions?.map((transaction, index) => {
                      return (
                        <tr className="text-center" key={index}>
                          <td>{transaction?.transactionId}</td>
                          <td>{transaction?.branchName ?? "---"}</td>
                          <td>{transaction?.orderId ?? "---"}</td>
                          <td>{transaction?.type?.toUpperCase()}</td>
                          <td>{transaction?.channel ? statusCard(transaction?.status) : "---"}</td>
                          <td>{transaction?.channel ?? "---"}</td>
                          <td>{transaction?.notes}</td>
                          <td>
                            {moment(transaction?.createdAt)
                              .tz(process.env.REACT_APP_TIMEZONE)
                              .format("Do MMM, YYYY")}
                          </td>
                          <td>
                            {moment(transaction?.createdAt)
                              .tz(process.env.REACT_APP_TIMEZONE)
                              .format("hh:mm A")}
                          </td>
                          <td>
                            <span
                              className={
                                transaction?.type === "credit"
                                  ? "badge text-white bg-success"
                                  : "badge text-white bg-danger"
                              }
                            >
                              {transaction?.type === "credit"
                                ? `+${formatDecimal(transaction?.amountInt)}`
                                : `-${formatDecimal(transaction?.amountInt)}`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {/*end::Table*/}
          {/*begin::Paginate*/}
          {count && Math.ceil(count / limit) > 1 ? (
            <Pagination
              page={page}
              setPage={setPage}
              count={count}
              limit={limit}
            />
          ) : (
            ""
          )}
          {/*end::Paginate*/}
        </div>
        {/*end::Card body*/}
      </div>
    </PermissionGate>
  );
};

export default TransactionTable;
